import { toast } from "sonner";

// Formların istemci tarafı zorunlu alan kontrolü.
//
// Formlar `noValidate` ile çalışır: Kaydet butonu formun dışında
// (form="..." bağlantılı) ve içerik sekmesi gizliyken tarayıcının yerel
// doğrulaması gönderimi yalnızca konsola uyarı yazarak sessizce engelliyordu.
// Bunun yerine handleSubmit başında burada kontrol edip kullanıcıyı ilgili
// sekme/dil/alana götürüyoruz.

export interface RequiredField<Target> {
  /** Kullanıcıya gösterilecek alan adı ("Başlık", "Belge 2: dosya"…) */
  label: string;
  /** Alanın boş sayılıp sayılmadığı */
  missing: boolean;
  /** Hata durumunda formun gideceği yer (sekme, dil, DOM id…) */
  target: Target;
}

// Zengin metin editörünün boş çıktısı ("<p></p>", "<p>&nbsp;</p>", "<br>"…)
// boş sayılır. Görsel içeren içerik metin olmasa da doludur.
export function isBlankHtml(html: string | null | undefined): boolean {
  if (!html) return true;
  if (/<(img|iframe|video|embed|object)\b/i.test(html)) return false;
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
    .replace(/[\s\u00a0\u200b]+/g, "");
  return text.length === 0;
}

export function isBlank(value: string | null | undefined): boolean {
  return !value || value.trim().length === 0;
}

// "Başlık alanı zorunludur." / "Başlık, Özet ve İçerik alanları zorunludur."
export function missingFieldsMessage(labels: string[]): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return `${labels[0]} alanı zorunludur.`;
  const head = labels.slice(0, -1).join(", ");
  return `${head} ve ${labels[labels.length - 1]} alanları zorunludur.`;
}

// Eksik alan bulunduğunda formun gideceği yer.
export interface FieldTarget<Tab extends string = string> {
  /** EntityTabs sekmesi (ör. "content", "media") */
  tab?: Tab;
  /** Alanın görünür olduğu dil */
  lang?: "tr" | "en";
  /** Odaklanacak elemanın DOM id'si (ya da onu saran kapsayıcının) */
  id: string;
}

export function findMissing<Target>(fields: RequiredField<Target>[]): RequiredField<Target>[] {
  return fields.filter((f) => f.missing);
}

// Zorunlu alanları kontrol eder. Eksik varsa ilk eksik alanın sekme/diline
// geçer (goTo), alana odaklanır ve tüm eksik alanları isimleriyle bildirir.
export function checkRequired<Tab extends string>(
  fields: RequiredField<FieldTarget<Tab>>[],
  goTo: (target: FieldTarget<Tab>) => void,
): boolean {
  const missing = findMissing(fields);
  if (missing.length === 0) return true;
  const { target } = missing[0];
  goTo(target);
  focusFieldSoon(target.id);
  toast.error(missingFieldsMessage(missing.map((f) => f.label)));
  return false;
}

// Tercih sırası: zengin metin editörü (araç çubuğu butonlarından önce),
// form alanları, en son buton (ör. ürün grubu seçici).
const FOCUSABLE_IN_ORDER = [
  '[contenteditable="true"]',
  'input:not([type="hidden"]), textarea, select',
  "button",
];

export function findFocusable(el: HTMLElement): HTMLElement | null {
  for (const selector of FOCUSABLE_IN_ORDER) {
    if (el.matches(selector)) return el;
    const inner = el.querySelector<HTMLElement>(selector);
    if (inner) return inner;
  }
  return null;
}

// Alanı görünür hale gelene kadar birkaç kare bekleyip odaklar ve ortalar.
// Sekme/dil değişimi sonrası alan henüz DOM'da olmayabilir (koşullu render,
// TipTap editörü bir effect sonrasında oluşur), bu yüzden kısa süre yeniden dener.
export function focusFieldSoon(id: string, attempts = 20): void {
  const tryFocus = (left: number) => {
    const el = document.getElementById(id);
    // Editör henüz oluşmadıysa (contenteditable yok) bir kare daha bekle
    const editable = el ? findFocusable(el) : null;
    const ready = el && el.getClientRects().length > 0 && editable;
    if (!ready) {
      if (left > 0) requestAnimationFrame(() => tryFocus(left - 1));
      else el?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    editable.focus({ preventScroll: true });
  };
  requestAnimationFrame(() => tryFocus(attempts));
}
